import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { FiUsers, FiRefreshCw, FiCheckCircle, FiDownload } from 'react-icons/fi';
import { reportsAPI } from '../../services/api';

const COLORS = ['#7c3aed', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#14b8a6'];

const SummaryView = ({ startDate, endDate, campus, gradeLevel }) => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [clusterDistribution, setClusterDistribution] = useState([]);
  const [gradeDistribution, setGradeDistribution] = useState([]);
  const [campusDistribution, setCampusDistribution] = useState([]);

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, JSON.stringify(campus), JSON.stringify(gradeLevel)]);

  const fetchReportData = async () => {
    try {
      setLoading(true);

      const [statsRes, clusterRes, gradeRes, campusRes] = await Promise.all([
        reportsAPI.getStatistics({ startDate, endDate, campus, gradeLevel }),
        reportsAPI.getClusterDistribution({ startDate, endDate, campus, gradeLevel }),
        reportsAPI.getGradeLevelDistribution({ startDate, endDate, campus, gradeLevel }),
        reportsAPI.getCampusDistribution({ startDate, endDate, campus, gradeLevel })
      ]);

      setStatistics(statsRes.data);
      setClusterDistribution(clusterRes.data);
      setGradeDistribution(gradeRes.data);
      setCampusDistribution(campusRes.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FiUsers}
          label="Total Responses"
          value={statistics?.totalResponses || 0}
          color="bg-purple-100 text-purple-600"
        />
        <StatCard
          icon={FiCheckCircle}
          label="Recent (30 days)"
          value={statistics?.recentResponses || 0}
          color="bg-green-100 text-green-600"
        />
        <StatCard
          icon={FiRefreshCw}
          label="Total Retakes"
          value={statistics?.totalRetakes || 0}
          color="bg-blue-100 text-blue-600"
        />
        <StatCard
          icon={FiDownload}
          label="Avg. Retakes"
          value={statistics?.avgRetakes?.toFixed(2) || '0.00'}
          color="bg-yellow-100 text-yellow-600"
        />
      </div>

      {/* Career Cluster Distribution */}
      {clusterDistribution.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Career Cluster Distribution
            <span className="ml-2 text-sm font-normal text-gray-500">
              (Student Selections)
            </span>
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={clusterDistribution}
                dataKey="count"
                nameKey="cluster"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={(entry) => `${entry.cluster.substring(0, 20)}${entry.cluster.length > 20 ? '...' : ''}`}
                labelLine={true}
              >
                {clusterDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                wrapperStyle={{ fontSize: '12px', maxHeight: '400px', overflowY: 'auto' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Grade Level Distribution */}
      {gradeDistribution.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Grade Level Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={gradeDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="grade"
                label={{ value: 'Grade Level', position: 'insideBottom', offset: -5 }}
              />
              <YAxis label={{ value: 'Number of Students', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="count" fill="#7c3aed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Campus Distribution */}
      {campusDistribution.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Campus Distribution</h3>
          <ResponsiveContainer width="100%" height={Math.max(300, campusDistribution.length * 40)}>
            <BarChart data={campusDistribution} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis
                type="category"
                dataKey="campus"
                width={150}
                style={{ fontSize: '12px' }}
              />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {clusterDistribution.length === 0 && gradeDistribution.length === 0 && campusDistribution.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-500">No data available for the selected time period</p>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="h-6 w-6" />
      </div>
    </div>
  </div>
);

export default SummaryView;
